import { CoachAssignmentPanel } from "@/components/CoachAssignment";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  coachWelcomeDraft,
  type CoachPatientDetail,
} from "@care-by-ella/contracts";
import { PatientChatPane } from "@/components/PatientChatPane";
import { PatientOpenHandoffs } from "@/components/PatientOpenHandoffs";
import { PatientGroupBadge } from "@/components/PatientGroupBadge";
import { CoachPatientHeader } from "@/components/CoachPatientHeader";
import { PatientWellnessProgram } from "@/components/PatientWellnessProgram";
import { PatientNutritionCourses } from "@/components/PatientNutritionCourses";
import { CoachWeeklyCheckins } from "@/components/CoachWeeklyCheckins";
import { F01StartContextPanel } from "@/components/F01StartContextPanel";
import { PatientMemoryPanel } from "@/components/PatientMemoryPanel";
import { PatientInBrief } from "@/components/PatientInBrief";
import { PatientCycleView } from "@/components/PatientCycleView";
import { WeeklyCheckinTrends } from "@/components/WeeklyCheckinTrends";
import { CoachNowPanel } from "@/components/CoachNowPanel";
import { CoachTopSummary } from "@/components/CoachTopSummary";
import { CoachHistoryPanel } from "@/components/CoachHistoryPanel";
import { OnboardingStatusCard } from "@/components/OnboardingStatusCard";
import {
  BookCareMeeting,
  useCareMeetingBookingAccess,
} from "@/components/BookCareMeeting";
import { useAuth } from "@/auth/AuthContext";
import { fetchCoachPatient, ForbiddenError } from "@/lib/clinician-api";
import { fetchPublishedCoachTemplate } from "@/lib/coach-templates-api";
import { treatmentStateLabel } from "@/lib/state-labels";
import {
  defaultCoachTab,
  HISTORY_ANCHOR,
  scrollTargetForHash,
  tabForHash,
  visibleCoachTabs,
  type CoachTab,
} from "@/lib/coach-tabs";
import {
  SupportAccessUntil,
  useReadOnly,
  useViewPath,
} from "@/components/SupportView";

// TES-493 — the coach's patient view.
//
// A separate route component from PatientDetailRoute rather than a role branch
// inside it. PatientDetailRoute composes a dozen panels that each fetch
// doctor-only data (labs, photos, transfer review, home-now preview, test
// mode); rendering it for a coach and hiding panels would still fire those
// requests. Not mounting it is the guarantee.
//
// Ö-58 (TES-475) ANSWERED, the coaching track — coach chat,
// reflections, wellbeing and "relevanta incheckningar". Never prescriptions,
// full lab results or clinical free text. TES-690 added the check-ins, which
// the answer had covered since September without anyone building them.
//
// TES-818 punkt 1 — the flat stack became three levels (Aktuellt,
// Patientbild, Historik). Emelie 2026-09-20 read the old page as "mainly chat
// plus empty Incheckningar/Wellnessprogram" and found no collected picture of
// the patient at all; the grouping is what makes each panel answer a question
// she is actually asking at that moment. Nothing was dropped in the move — the
// same five things are here, sorted by what they are for.
//
// TES-1206 — and the levels became tabs. A-01's later decision replaces the
// single scroll page: "Den fortsatta patientarbetsytan delas i separata flikar: Chatt, Incheckningar och Patientbild."
// Acceptance: "En coach kan förstå vem patienten är och var hon befinner sig utan att skrolla genom hela sidan."
// The header, the top summary and "Just nu" stay above the tabs; Historik
// (TES-1207) is Patientbild's folded sub-level and replaces the dashed
// "Tidigare kontakt" placeholder. The hash picks the tab, so every existing
// deep link still lands (lib/coach-tabs.ts).

export function CoachPatientDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { signOut, claims } = useAuth();
  // TES-1040 — the admin's read-only support view: no chat, links stay in
  // the view.
  const readOnly = useReadOnly();
  const viewPath = useViewPath();
  // TES-1219 — the admin (in her support view, or a coach with the admin
  // grant) books a care meeting from here too; the plain coach never does.
  const bookingAccess = useCareMeetingBookingAccess();
  const [data, setData] = useState<CoachPatientDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // TES-1074 — bumped on every reload of the page's own data, so "Just nu"
  // follows a check-in that was just handled.
  const [loadCount, setLoadCount] = useState(0);
  // TES-1074 — bumped by Patientminnet after a write, so the summary that is
  // composed from it refetches.
  const [memoryVersion, setMemoryVersion] = useState(0);
  // TES-1352 — "Rätta i Patientminnet" from an AI draft's "Underlag": the
  // entry to open in the Patientbild tab's own correction form. `n` makes a
  // second click on the same entry a new request.
  const [correctMemory, setCorrectMemory] = useState<{
    itemId: string;
    n: number;
  } | null>(null);

  // TES-1206 — the tab follows the hash: a link into a tab opens it, a link
  // to something above the tabs (#handoff, #aktuellt) keeps the current one.
  // The read-only support view has no chat, so no Chatt tab either.
  const location = useLocation();
  const navigate = useNavigate();
  const tabs = visibleCoachTabs(readOnly);
  const [tab, setTab] = useState<CoachTab>(
    () => tabForHash(location.hash, readOnly) ?? defaultCoachTab(readOnly),
  );
  const [historyOpen, setHistoryOpen] = useState(
    location.hash === `#${HISTORY_ANCHOR}`,
  );
  useEffect(() => {
    const fromHash = tabForHash(location.hash, readOnly);
    if (fromHash) setTab(fromHash);
    if (location.hash === `#${HISTORY_ANCHOR}`) setHistoryOpen(true);
  }, [location.hash, readOnly]);

  const selectTab = (next: CoachTab) => {
    setTab(next);
    navigate({ search: location.search, hash: `#${next}` }, { replace: true });
  };

  // A link to an element inside a tab: once the tab shows and the panel has
  // rendered its rows, bring the element into view. The panels fetch on
  // their own, so the target may arrive a moment after the tab does.
  const loaded = data !== null;
  useEffect(() => {
    const target = scrollTargetForHash(location.hash);
    if (!target || !loaded) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.getElementById(target);
      if ((el && el.getClientRects().length > 0) || ++tries > 30) {
        window.clearInterval(timer);
        el?.scrollIntoView({ block: "start" });
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [location.hash, tab, loaded]);

  // From lg up the header is pinned too, so the tab bar pins right under it
  // rather than over it; its height changes with what the header holds.
  const headerRef = useRef<HTMLElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setHeaderHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [loaded]);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    try {
      setData(await fetchCoachPatient(id));
      setLoadCount((n) => n + 1);
    } catch (err) {
      setData(null);
      setError(
        err instanceof ForbiddenError
          ? err.message
          : err instanceof Error
            ? err.message
            : "okänt fel",
      );
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // TES-1355 — the welcome draft is built from the template the admin
  // published under Coachmallar. The chat composer takes its draft once, on
  // mount, so the chat waits for the template: undefined while it loads,
  // null when nothing is published or the read failed, and the draft then
  // falls back to the A-01 text in code.
  const wantsWelcome = !readOnly && searchParams.get("utkast") === "valkomst";
  const [welcomeTemplate, setWelcomeTemplate] = useState<
    string | null | undefined
  >(undefined);
  useEffect(() => {
    if (!wantsWelcome) return;
    let live = true;
    fetchPublishedCoachTemplate("welcome")
      .then((t) => live && setWelcomeTemplate(t.body))
      .catch(() => live && setWelcomeTemplate(null));
    return () => {
      live = false;
    };
  }, [wantsWelcome]);

  const back = (
    <Link
      to={viewPath("/patients")}
      className="text-sm text-brand hover:underline"
    >
      ← Alla patienter
    </Link>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-md items-start">
        {back}
        <div className="border border-danger rounded-md p-md text-sm bg-surface flex flex-col gap-sm items-start">
          <p className="text-danger">Patienten kunde inte hämtas ({error}).</p>
          <button
            type="button"
            onClick={() => void load()}
            className="px-md py-sm rounded-md border border-surfaceMuted"
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-md" aria-busy="true">
        {back}
        <div className="h-24 rounded-md border border-surfaceMuted bg-surfaceMuted/30 animate-pulse" />
        <div className="h-64 rounded-md border border-surfaceMuted bg-surfaceMuted/30 animate-pulse" />
      </div>
    );
  }

  const { patient, treatment, patientGroup } = data;

  return (
    <div
      className="flex flex-col gap-lg"
      style={{ "--coach-header-h": `${headerHeight}px` } as CSSProperties}
    >
      {/* TES-1074 point 1 — the persistent header. The treatment line moved
          into it ("Behandling"); an older API without `header` keeps it here. */}
      <CoachPatientHeader
        ref={headerRef}
        header={data.header}
        title={
          <div className="flex flex-col gap-sm">
            {back}
            <div className="flex items-center gap-sm flex-wrap">
              <h1 className="font-display text-3xl tracking-tight">
                {patient.name ?? "Patient utan namn"}
              </h1>
              <PatientGroupBadge group={patientGroup} />
              {patient.isTest ? (
                <span className="text-xs px-sm py-xs rounded-md border border-surfaceMuted">
                  Testpatient
                </span>
              ) : null}
            </div>
            {data.header ? null : (
              <p className="text-sm text-textSecondary">
                {treatment
                  ? `Behandling: ${treatmentStateLabel(treatment.state)}`
                  : "Ingen behandling påbörjad"}
              </p>
            )}
            <p className="text-xs text-textSecondary">
              <SupportAccessUntil patientId={patient.id} />
            </p>
          </div>
        }
      />

      {/* TES-1209 — A-01: "På patientöversikten ska ett framträdande
          onboardingkort visa patientresa, aktuellt steg, nästa steg, senaste
          relevanta aktivitet med datum och vem systemet väntar på." The
          doctor's card, from the same derivation; the server sends it only
          while she is in onboarding, with the doctor's review and decision as
          dated steps without their outcome. */}
      <OnboardingStatusCard
        status={data.onboardingStatus}
        timeline={data.onboardingTimeline}
      />

      {/* TES-1101 (Ö-320–323) — the fast top summary under the header: what
          she mainly wants help with, goals and needs, the next coach task,
          handoffs and the neutral medical status. The sum of the two
          counters changes whenever either does: a reload of the page's data,
          or a write in Patientminnet. */}
      <CoachTopSummary
        patientId={patient.id}
        refreshKey={loadCount + memoryVersion}
        onUnauthorized={() => void signOut()}
      />

      {/* TES-1206 — what is happening now stays above the tabs, visible from
          every one of them: A-01's overview names "nästa viktiga steg …
          öppen överenskommelse eller planerad uppföljning", which is what
          this panel holds. The handoffs to her land here too (#handoff);
          the component renders nothing when there are none. */}
      <div id="aktuellt" className="flex flex-col gap-md">
        <CoachNowPanel
          patientId={patient.id}
          refreshKey={loadCount}
          onUnauthorized={() => void signOut()}
        />
        <PatientOpenHandoffs patientId={patient.id} />
        {bookingAccess !== "none" ? (
          <BookCareMeeting
            key={`book-meeting:${patient.id}`}
            patientId={patient.id}
            onBooked={() => void load()}
            onUnauthorized={() => void signOut()}
          />
        ) : null}
      </div>

      {/* TES-1206 — the workspace as tabs, A-01's "separata flikar". The bar
          sticks to the top so another tab, the chat included, is one click
          away however far down a tab she has read. Every panel stays mounted
          and is only hidden: a half-written chat message survives a look at
          the check-ins, and a link into a hidden tab finds its target. */}
      <div className="flex flex-col gap-md">
        <div
          role="tablist"
          aria-label="Patientens arbetsyta"
          className="sticky top-0 z-20 lg:top-[var(--coach-header-h,0px)] flex gap-lg overflow-x-auto overflow-y-hidden border-b border-surfaceMuted bg-background pt-sm"
        >
          {tabs.map((t, i) => (
            <button
              key={t.id}
              id={`flik-${t.id}`}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              aria-controls={`panel-${t.id}`}
              tabIndex={tab === t.id ? 0 : -1}
              onClick={() => selectTab(t.id)}
              onKeyDown={(e) => {
                const step =
                  e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
                if (step === 0) return;
                e.preventDefault();
                const next = tabs[(i + step + tabs.length) % tabs.length];
                selectTab(next.id);
                document.getElementById(`flik-${next.id}`)?.focus();
              }}
              className={`shrink-0 whitespace-nowrap pb-sm -mb-px border-b-2 text-sm transition ${
                tab === t.id
                  ? "border-brand text-textPrimary font-medium"
                  : "border-transparent text-textSecondary hover:text-textPrimary"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* CHATT — the coach dialogue, shared reflections included (they
            arrive as ordinary messages marked "Reflektion", Ö-167). The
            admin's read-only support view has no chat and no tab for it. */}
        {readOnly ? null : (
          <TabPanel id="chatt" active={tab === "chatt"}>
            {wantsWelcome && welcomeTemplate === undefined ? (
              <p className="text-sm text-textSecondary">
                Hämtar välkomstmallen…
              </p>
            ) : (
              <PatientChatPane
                patientId={patient.id}
                onUnauthorized={() => void signOut()}
                onCorrectMemory={(itemId) => {
                  selectTab("patientbild");
                  setCorrectMemory((c) => ({ itemId, n: (c?.n ?? 0) + 1 }));
                }}
                // TES-1189 — "Skriv välkomstmeddelande" lands here with
                // ?utkast=valkomst: the coach chat opens with the welcome
                // draft, editable and unsent. TES-1344 — signed by the coach
                // who writes it, never the tenant persona: the case is routed
                // to the patient's primary coach only (TES-1218). Without a
                // name on her account there is no honest "Jag heter …", so
                // the composer opens empty instead.
                initialDraft={
                  wantsWelcome && claims?.displayName?.trim()
                    ? coachWelcomeDraft({
                        coachName: claims.displayName,
                        patientName: patient.name,
                        template: welcomeTemplate,
                      })
                    : undefined
                }
              />
            )}
          </TabPanel>
        )}

        {/* INCHECKNINGAR — A-01: "aktuella och tidigare coachrelevanta
            veckoincheckningar, återkoppling och relevanta trender". The
            server has already dropped anything that escalated to the
            doctor (TES-690). */}
        <TabPanel id="incheckningar" active={tab === "incheckningar"}>
          <CoachWeeklyCheckins
            patientId={patient.id}
            checkins={data.weeklyCheckins}
            onReviewed={() => void load()}
            onUnauthorized={() => void signOut()}
          />
          <WeeklyCheckinTrends
            patientId={patient.id}
            onUnauthorized={() => void signOut()}
          />
        </TabPanel>

        {/* PATIENTBILD — the context that survives between contacts, with
            Historik as its folded sub-level. */}
        <TabPanel id="patientbild" active={tab === "patientbild"}>
          {/* Where the rest of the clinical picture is: the question a coach
              asks at this level, where she would otherwise keep hunting for
              labs and prescriptions. */}
          <p className="max-w-2xl rounded-md border border-surfaceMuted bg-surface p-sm text-xs text-textSecondary">
            Coachvyn visar det du behöver för coachningen. Recept, provsvar och
            journaltext ligger hos läkaren — behöver du något därifrån, skicka
            en överlämning.
          </p>
          {/* TES-1074 points 2–4 — "Patienten i korthet" first, the coach's
              primary overview; Patientminnet below it is the editable,
              source-traceable depth it is composed from; F-01's original
              answers last, as the source both of them point back to. */}
          <PatientInBrief
            patientId={patient.id}
            refreshKey={memoryVersion}
            onUnauthorized={() => void signOut()}
          />
          {/* TES-1218 (Ö-407) — her primary coach and the change log; an
              admin can reassign from here. */}
          <section className="rounded-md border border-surfaceMuted bg-surface p-md">
            <h2 className="mb-xs font-display text-lg">Primär coach</h2>
            <CoachAssignmentPanel
              patientId={patient.id}
              onUnauthorized={() => void signOut()}
            />
          </section>
          <PatientMemoryPanel
            patientId={patient.id}
            onChanged={() => setMemoryVersion((v) => v + 1)}
            correct={correctMemory}
          />
          <F01StartContextPanel
            patientId={patient.id}
            onUnauthorized={() => void signOut()}
          />
          {/* The cycle: a coach reads it to know where in the treatment
              month the patient is, not to act on it. */}
          <PatientCycleView patientId={patient.id} />
          {/* The active version is the plan the patient is following right
              now, not an event. */}
          <PatientWellnessProgram patientId={patient.id} />
          {/* TES-1067 — recommend a "Stöd → Kost" course; her protein target. */}
          <PatientNutritionCourses patientId={patient.id} />
          {/* TES-1207 — what has been, folded: "för fördjupning, inte
              startsida". */}
          <CoachHistoryPanel
            patientId={patient.id}
            open={historyOpen}
            onToggle={setHistoryOpen}
            onUnauthorized={() => void signOut()}
          />
        </TabPanel>
      </div>
    </div>
  );
}

function TabPanel({
  id,
  active,
  children,
}: {
  id: CoachTab;
  active: boolean;
  children: ReactNode;
}) {
  return (
    <section
      id={`panel-${id}`}
      role="tabpanel"
      aria-labelledby={`flik-${id}`}
      hidden={!active}
      // Not `flex` while hidden: the utility would override [hidden].
      className={active ? "flex flex-col gap-md" : "hidden"}
    >
      {children}
    </section>
  );
}
